import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createFilter,
  updateFilter,
  deleteFilter,
} from "../../redux/actions/filters";
import { useToast } from "../Layout/Toast/ToastProvider";
import { Loader } from "../Layout/Loader";
import "./CreateFilter.scss";

const CreateFilter = () => {
  const dispatch = useDispatch();

  const { filters, isLoading, error } = useSelector((state) => state.filters);
  const { categories } = useSelector((state) => state.category);
  const { subcategories } = useSelector((state) => state.subcategories);
  const { subsubcategories } = useSelector((state) => state.subsubcategories);
  const { showToast } = useToast();

  const [filterData, setFilterData] = useState({
    name: "",
    displayName: "",
    categoryId: [],
    subcategoryId: [],
    subsubcategoryId: [],
    isRange: false,
    range: { min: "", max: "" },
    values: [],
  });

  const [editMode, setEditMode] = useState(false);
  const [editFilterId, setEditFilterId] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRangeChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({
      ...prev,
      range: { ...prev.range, [name]: value },
    }));
  };

  const handleCategoryChange = (e) => {
    const { value, checked } = e.target;
    setFilterData((prevData) => {
      const updatedCategories = checked
        ? [...prevData.categoryId, value]
        : prevData.categoryId.filter((id) => id !== value);

      return { ...prevData, categoryId: updatedCategories };
    });
  };

  const handleSubcategoryChange = (e) => {
    const { value, checked } = e.target;
    setFilterData((prevData) => {
      const updatedSubcategories = checked
        ? [...prevData.subcategoryId, value]
        : prevData.subcategoryId.filter((id) => id !== value);

      return { ...prevData, subcategoryId: updatedSubcategories };
    });
  };

  const handleSubSubcategoryChange = (e) => {
    const { value, checked } = e.target;
    setFilterData((prevData) => {
      const updatedSubSubcategories = checked
        ? [...prevData.subsubcategoryId, value]
        : prevData.subsubcategoryId.filter((id) => id !== value);

      return { ...prevData, subsubcategoryId: updatedSubSubcategories };
    });
  };

  const handleValuesChange = (e, index) => {
    const newValues = [...filterData.values];
    newValues[index] = {
      ...newValues[index],
      value: e.target.value,
    };
    setFilterData({ ...filterData, values: newValues });
  };

  const addValueField = () => {
    setFilterData((prev) => ({
      ...prev,
      values: [...prev.values, { value: "" }],
    }));
  };

  const removeValueField = (index) => {
    const newValues = [...filterData.values];
    newValues.splice(index, 1);
    setFilterData({ ...filterData, values: newValues });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (editMode) {
      dispatch(updateFilter(editFilterId, filterData))
        .then(() => {
          showToast("Фільтр успішно оновлено!", "success");
          resetForm();
        })
        .catch(() => {
          showToast("Помилка при оновленні фільтру!", "error");
        });
    } else {
      dispatch(createFilter(filterData))
        .then(() => {
          showToast("Фільтр успішно створено!", "success");
          resetForm();
        })
        .catch(() => {
          showToast("Помилка при створенні фільтру!", "error");
        });
    }
  };

  const handleEdit = (filter) => {
    setEditMode(true);
    setEditFilterId(filter._id);
    const updatedValues = (filter.values || []).map((val) => {
      if (typeof val === "string") {
        return { value: val };
      }
      return val;
    });

    setFilterData({
      name: filter.name,
      displayName: filter.displayName,
      categoryId: filter.categoryId
        ? filter.categoryId.map((cat) => cat._id.toString())
        : [],
      subcategoryId: filter.subcategoryId
        ? filter.subcategoryId.map((sub) => sub._id.toString())
        : [],
      subsubcategoryId: filter.subsubcategoryId
        ? filter.subsubcategoryId.map((subsub) => subsub._id.toString())
        : [],
      isRange: filter.isRange,
      range: filter.range || { min: "", max: "" },
      values: updatedValues,
    });
  };

  const handleDelete = (id) => {
    if (window.confirm("Ви впевнені, що хочете видалити цей фільтр?")) {
      dispatch(deleteFilter(id))
        .then(() => {
          showToast("Фільтр успішно видалено!", "success");
        })
        .catch(() => {
          showToast("Помилка при видаленні фільтру!", "error");
        });
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setEditFilterId(null);
    setFilterData({
      name: "",
      displayName: "",
      categoryId: [],
      subcategoryId: [],
      subsubcategoryId: [],
      isRange: false,
      range: { min: "", max: "" },
      values: [],
    });
  };

  return (
    <div className="create-filter-container">
      <h2 className="create-filter-title">Керування фільтрами</h2>

      {error && <p className="error">{error}</p>}
      {isLoading && <Loader />}

      <form onSubmit={handleSubmit} className="create-filter-form">
        <div className="contain">
          <div className="mb-45">
            <label className="form-label">Ім'я фільтру:</label>
            <input
              type="text"
              name="name"
              value={filterData.name}
              onChange={handleInputChange}
              className="form-input1"
              required
            />
          </div>

          <div className="mb-45">
            <label className="form-label">Ім'я для відображення (для клієнта):</label>
            <input
              type="text"
              name="displayName"
              value={filterData.displayName}
              onChange={handleInputChange}
              className="form-input1"
              required
            />
          </div>
        </div>

        {/* Категорії */}
        <div className="mb-4">
          <label className="form-label">Категорії:</label>
          <div className="grid-2col">
            {categories.map((category) => (
              <div key={category._id} className="flex-center">
                <input
                  type="checkbox"
                  id={category._id}
                  value={category._id}
                  checked={filterData.categoryId.includes(
                    category._id.toString()
                  )}
                  onChange={handleCategoryChange}
                  className="mr-2"
                />
                <label htmlFor={category._id} className="text-gray-700">
                  {category.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Субкатегорії */}
        <div className="mb-4">
          <label className="form-label">Суб категорії:</label>
          <div className="grid-2col">
            {subcategories &&
              subcategories.map((subcategory) => (
                <div key={subcategory._id} className="flex-center">
                  <input
                    type="checkbox"
                    id={subcategory._id}
                    value={subcategory._id}
                    checked={filterData.subcategoryId.includes(
                      subcategory._id.toString()
                    )}
                    onChange={handleSubcategoryChange}
                    className="mr-2"
                  />
                  <label htmlFor={subcategory._id} className="text-gray-700">
                    {subcategory.name}
                  </label>
                </div>
              ))}
          </div>
        </div>

        {/* Суб-субкатегорії */}
        <div className="mb-4">
          <label className="form-label">Суб-суб категорії:</label>
          <div className="grid-2col">
            {subsubcategories &&
              subsubcategories.map((subsubcategory) => (
                <div key={subsubcategory._id} className="flex-center">
                  <input
                    type="checkbox"
                    id={subsubcategory._id}
                    value={subsubcategory._id}
                    checked={filterData.subsubcategoryId.includes(
                      subsubcategory._id.toString()
                    )}
                    onChange={handleSubSubcategoryChange}
                    className="mr-2"
                  />
                  <label htmlFor={subsubcategory._id} className="text-gray-700">
                    {subsubcategory.name}
                  </label>
                </div>
              ))}
          </div>
        </div>

        {/* Чи це діапазон? */}
        <div className="mb-4">
          <label className="form-label">
            Якщо фільтр діапазону: (Наприклад від 1000 до 9000)
          </label>
          <input
            type="checkbox"
            name="isRange"
            checked={filterData.isRange}
            onChange={(e) =>
              setFilterData((prev) => ({ ...prev, isRange: e.target.checked }))
            }
            className="mr-2"
          />
        </div>

        {/* Якщо фільтр діапазону - показуємо поля для min і max */}
        {filterData.isRange && (
          <div className="flex-center mb-4" style={{ gap: "1rem" }}>
            <div>
              <label className="form-label">Мінімум:</label>
              <input
                type="number"
                name="min"
                value={filterData.range.min}
                onChange={handleRangeChange}
                className="form-input"
                required
              />
            </div>
            <div>
              <label className="form-label">Максимум:</label>
              <input
                type="number"
                name="max"
                value={filterData.range.max}
                onChange={handleRangeChange}
                className="form-input"
                required
              />
            </div>
          </div>
        )}

        {/* Якщо НЕ діапазон - показуємо варіанти */}
        {!filterData.isRange && (
          <div className="mb-4">
            <label className="form-label">Варіанти:</label>
            <div className="valsp">
              {filterData.values.map((valObj, index) => (
                <div
                  key={valObj._id || index}
                  className="flex-center mb-4"
                  style={{ gap: "0.5rem" }}
                >
                  <input
                    type="text"
                    value={valObj.value}
                    onChange={(e) => handleValuesChange(e, index)}
                    className="form-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeValueField(index)}
                    className="btn-remove-value"
                  >
                    Видалити
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addValueField}
              className="btn-add-value"
            >
              Додати варіант
            </button>
          </div>
        )}

        <div className="btn-group">
          <button type="submit" className="btn-submit">
            {editMode ? "Оновити фільтр" : "Створити Фільтр"}
          </button>
          {editMode && (
            <button type="button" onClick={resetForm} className="btn-cancel">
              Скасувати
            </button>
          )}
        </div>
      </form>

      {/* Список існуючих фільтрів */}
      <div>
        <h3 className="existing-filters-title">Існуючі Фільтри</h3>
        {filters.map((filter) => (
          <div key={filter._id} className="existing-filter-item">
            <h4 className="existing-filter-item-title">{filter.displayName}</h4>
            <p>
              Categories:{" "}
              {filter.categoryId
                ?.map((cat) => cat.name)
                .join(", ")}
            </p>
            <p>
              Subcategories:{" "}
              {filter.subcategoryId
                ?.map((sub) => sub.name)
                .join(", ")}
            </p>
            <p>
              Subsubcategories:{" "}
              {filter.subsubcategoryId
                ?.map((subsub) => subsub.name)
                .join(", ")}
            </p>

            {filter.isRange ? (
              <p>
                Range: {filter.range?.min} - {filter.range?.max}
              </p>
            ) : (
              <ul>
                {filter.values?.map((valObj) => (
                  <li key={valObj._id || Math.random()}>
                    {valObj.value}
                  </li>
                ))}
              </ul>
            )}

            <div className="btn-group">
              <button onClick={() => handleEdit(filter)} className="btn-edit">
                Редагувати
              </button>
              <button
                onClick={() => handleDelete(filter._id)}
                className="btn-delete"
              >
                Видалити
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CreateFilter;
